import {createTheme} from "@mui/material/styles";

// Stessi valori di :root (chiaro) e di [data-theme="dark"] in style/index.scss
// (verificati da utils.test.js). I valori chiari servono anche per stampa e pagine pubbliche.
export const LIGHT_COLORS = {
	primary: "#1059bf",
	primaryDark: "#0b1f42",
	text: "#0b1f44",
	textSecondary: "#64748b",
	textMuted: "#94a3b8",
	border: "#e2e8f0",
	bg: "#f1f5f9",
	surface: "#ffffff",
	surfaceSoft: "#f8fafc",
	good: "#15803d",
	goodBg: "#dcfce7",
	warning: "#b45309",
	warningBg: "#fef3c7",
	critical: "#b91c1c",
	criticalBg: "#fee2e2",
};

export const DARK_COLORS = {
	...LIGHT_COLORS,
	primary: "#3a7be6",
	text: "#e7edf6",
	textSecondary: "#a3b2c9",
	textMuted: "#7a8ba6",
	border: "#26354f",
	bg: "#0a1426",
	surface: "#111c33",
	surfaceSoft: "#16233d",
	good: "#4ade80",
	goodBg: "#123524",
	warning: "#fbbf24",
	warningBg: "#3a2c0b",
	critical: "#f87171",
	criticalBg: "#3d1518",
};

// Tema: preferenza del dispositivo (Il mio account): chiaro, scuro o come il dispositivo.
// Le pagine pubbliche, aperte da chi riceve un link, restano sempre chiare.
const THEME_KEY = "ui_theme";

export const getThemePreference = () => {
	try {
		return localStorage.getItem(THEME_KEY) || "light";
	} catch {
		return "light";
	}
};

const systemPrefersDark = () =>
	typeof window !== "undefined" &&
	!!window.matchMedia?.("(prefers-color-scheme: dark)").matches;

export const activeTheme = () => {
	if (typeof window === "undefined") return "light";
	if (window.location.pathname.startsWith("/public")) return "light";
	const preference = getThemePreference();
	if (preference === "system") return systemPrefersDark() ? "dark" : "light";
	return preference === "dark" ? "dark" : "light";
};

export const applyTheme = () => {
	document.documentElement.dataset.theme = activeTheme();
};

// I colori JS (grafici, tema MUI) si leggono all'avvio: al cambio si ricarica la pagina
export const setThemePreference = (preference) => {
	try {
		localStorage.setItem(THEME_KEY, preference);
	} catch {
		// senza memoria del browser la scelta non si può tenere
	}
	window.location.reload();
};

export const COLORS = activeTheme() === "dark" ? DARK_COLORS : LIGHT_COLORS;

// Per stampe e finestre senza i nostri stili: valori chiari al posto dei var(--color-…)
const PRIMARY_RGB = "16, 89, 191";
export const printColor = (value) =>
	String(value ?? "")
		.replace(
			/var\(--color-primary-alpha-(\d+)\)/g,
			(_, n) => `rgba(${PRIMARY_RGB}, ${Number(n) / 100})`,
		)
		.replace(/var\(--color-([a-z-]+)\)/g, (match, name) => {
			const key = name.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
			return LIGHT_COLORS[key] || match;
		});

// Serie dei grafici per categorie (tipi, fasi): ordine fisso, mai ciclato a caso.
// Validate per daltonismo sul fondo di ciascun tema; sempre con legenda o etichetta.
const LIGHT_SERIES = [
	LIGHT_COLORS.primary,
	"#eb6834",
	"#1baf7a",
	"#eda100",
	"#e87ba4",
	"#008300",
	"#6250d6",
	"#e34948",
];

const DARK_SERIES = [
	DARK_COLORS.primary,
	"#d45c2c",
	"#1aa673",
	"#c28400",
	"#c9608a",
	"#1b8f1b",
	"#7565e2",
	"#de4645",
];

export const SERIES = activeTheme() === "dark" ? DARK_SERIES : LIGHT_SERIES;

// Stessi caratteri di --font-sans in style/index.scss
export const FONT_SANS =
	'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export const theme = createTheme({
	typography: {fontFamily: FONT_SANS},
	palette: {
		mode: activeTheme(),
		blue: {
			main: COLORS.primary,
			dark: COLORS.primaryDark,
		},
		text: {
			primary: COLORS.text,
			secondary: COLORS.textSecondary,
		},
		background: {
			default: COLORS.bg,
			paper: COLORS.surface,
		},
		divider: COLORS.border,
	},
});

// Effetto vetro: preferenza del dispositivo (Il mio account), acceso salvo scelta contraria.
// Si applica come data-glass="on|off" sulla radice: gli stili in style/index.scss lo leggono.
const GLASS_KEY = "ui_glass";

export const isGlassOn = () => {
	try {
		return localStorage.getItem(GLASS_KEY) !== "off";
	} catch {
		return true;
	}
};

export const applyGlass = (on = isGlassOn()) => {
	document.documentElement.dataset.glass = on ? "on" : "off";
};

export const setGlass = (on) => {
	try {
		localStorage.setItem(GLASS_KEY, on ? "on" : "off");
	} catch {
		// senza memoria del browser la scelta vale solo per questa visita
	}
	applyGlass(on);
};
